import { taggedTemplateLiteral as _taggedTemplateLiteral, objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends, asyncToGenerator as _asyncToGenerator, regeneratorRuntime as _regeneratorRuntime } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import styled__default from 'styled-components';
import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import _Box from '../../components/Box/index.js';
import useDebounce from '../../hooks/useDebounce.js';
import useOutsideClick from '../../hooks/useOutsideClick.js';
import CustomPromise from '../../../node_modules/p-cancelable/index.js';
import { v4 } from 'uuid';
import useMemoize from '../../hooks/useMemoize.js';
import SearchContext from './SearchContext.js';
import TagInput from './TagInput.js';
import { TextInput } from '../../index.js';

var _excluded = ["debounce", "disableResultsDropdown", "emptyResultComponent", "field", "label", "onQueryChange", "onResultsChange", "resultComponent", "resultBoxStyles", "resolveOnFocus", "focusAfterEnter", "hideResultsRef", "showResultsOnEmptyQuery", "groups", "resultGroupComponent", "showTags", "getTagApi", "onFocus"];
var _templateObject, _templateObject2;
var StyledSearchContainer = styled__default('div')(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  position: relative;\n"])));
var StyledResultsBox = styled__default(_Box)(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  padding: 0;\n  position: absolute;\n  width: 100%;\n  z-index: 1000;\n  overflow-y: scroll;\n"])));
function Search(_ref) {
  var _ref$debounce = _ref.debounce,
    debounce = _ref$debounce === void 0 ? 250 : _ref$debounce,
    _ref$disableResultsDr = _ref.disableResultsDropdown,
    disableResultsDropdown = _ref$disableResultsDr === void 0 ? false : _ref$disableResultsDr,
    _ref$emptyResultCompo = _ref.emptyResultComponent,
    EmptyResultsComponent = _ref$emptyResultCompo === void 0 ? function () {
      return /*#__PURE__*/React.createElement(React.Fragment, null);
    } : _ref$emptyResultCompo,
    field = _ref.field,
    label = _ref.label,
    onQueryChange = _ref.onQueryChange,
    _ref$onResultsChange = _ref.onResultsChange,
    onResultsChange = _ref$onResultsChange === void 0 ? function () {} : _ref$onResultsChange,
    _ref$resultComponent = _ref.resultComponent,
    ResultComponent = _ref$resultComponent === void 0 ? function () {
      return /*#__PURE__*/React.createElement(React.Fragment, null);
    } : _ref$resultComponent,
    _ref$resultBoxStyles = _ref.resultBoxStyles,
    resultBoxStyles = _ref$resultBoxStyles === void 0 ? {} : _ref$resultBoxStyles,
    _ref$resolveOnFocus = _ref.resolveOnFocus,
    resolveOnFocus = _ref$resolveOnFocus === void 0 ? false : _ref$resolveOnFocus,
    focusAfterEnter = _ref.focusAfterEnter,
    hideResultsRef = _ref.hideResultsRef,
    showResultsOnEmptyQuery = _ref.showResultsOnEmptyQuery,
    groups = _ref.groups,
    _ref$resultGroupCompo = _ref.resultGroupComponent,
    ResultGroupComponent = _ref$resultGroupCompo === void 0 ? function () {
      return /*#__PURE__*/React.createElement(React.Fragment, null);
    } : _ref$resultGroupCompo,
    _ref$showTags = _ref.showTags,
    showTags = _ref$showTags === void 0 ? false : _ref$showTags,
    getTagApi = _ref.getTagApi,
    onFocus = _ref.onFocus,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    query = _useState2[0],
    setQuery = _useState2[1];
  var _useDebounce = useDebounce(query, debounce),
    _useDebounce2 = _slicedToArray(_useDebounce, 2),
    debouncedQuery = _useDebounce2[0],
    cancelUpdate = _useDebounce2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    results = _useState4[0],
    setResults = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isDirty = _useState6[0],
    setIsDirty = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isLoading = _useState8[0],
    setIsLoading = _useState8[1];
  var _useState9 = useState(true),
    _useState10 = _slicedToArray(_useState9, 2),
    areResultsShown = _useState10[0],
    setAreResultsShown = _useState10[1];
  var _useState11 = useState(),
    _useState12 = _slicedToArray(_useState11, 2),
    previousRequest = _useState12[0],
    setPreviousRequest = _useState12[1];
  var getResults = useMemoize(CustomPromise.fn(onQueryChange));
  var _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    inputFocus = _useState14[0],
    setInputFocus = _useState14[1];
  // I've come to the conclusion that no-one has any idea what's going
  // on w/ types and react
  var inputRef = useRef();
  var searchResultsRef = useRef();
  var handleChange = function handleChange(_query) {
    setQuery(_query);
  };
  var hideResults = function hideResults() {
    return setAreResultsShown(false);
  };
  if (hideResultsRef) {
    hideResultsRef.current = hideResults;
  }
  var clearResults = function clearResults() {
    return setResults([]);
  };
  useOutsideClick([inputRef, searchResultsRef], hideResults);
  var resolveResults = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
      var request, _results;
      return _regeneratorRuntime().wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            setIsLoading(true);
            // Cancel previous request, queue up new one.
            if (!!previousRequest) {
              previousRequest.cancel();
            }
            request = getResults(debouncedQuery);
            setPreviousRequest(request);
            _context.prev = 4;
            _context.next = 7;
            return request;
          case 7:
            _results = _context.sent;
            setResults(_results);
            _context.next = 14;
            break;
          case 11:
            _context.prev = 11;
            _context.t0 = _context["catch"](4);
            if (!request.isCanceled) ;
          case 14:
            _context.prev = 14;
            setIsLoading(false);
            return _context.finish(14);
          case 17:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[4, 11, 14, 17]]);
    }));
    return function resolveResults() {
      return _ref2.apply(this, arguments);
    };
  }();
  var handleFocus = function handleFocus(e) {
    if (resolveOnFocus && debouncedQuery && debouncedQuery.length > 0 || resolveOnFocus && showResultsOnEmptyQuery) {
      // @ts-ignore
      getResults.cache.clear();
      clearResults();
      resolveResults();
      if (onFocus) {
        onFocus(e);
      }
    }
    setAreResultsShown(true);
  };
  var onKeyDownHandler = function onKeyDownHandler(event) {
    if (event.keyCode === 13) {
      if (focusAfterEnter && focusAfterEnter.current) {
        focusAfterEnter.current.focus();
      }
    }
  };
  useLayoutEffect(function () {
    if (isDirty && (query && query.length > 0 || showResultsOnEmptyQuery)) {
      resolveResults();
    } else {
      cancelUpdate();
      clearResults();
    }
  }, [debouncedQuery]);
  useEffect(function () {
    if (query && query.length > 0 && !isDirty) {
      setIsDirty(true);
    }
  }, [query]);
  useEffect(function () {
    if (isDirty) {
      onResultsChange(results);
    }
  }, [results]);
  var value = {
    hideResults: hideResults
  };
  return /*#__PURE__*/React.createElement(SearchContext.Provider, {
    value: value
  }, /*#__PURE__*/React.createElement(StyledSearchContainer, null, showTags ? /*#__PURE__*/React.createElement(TagInput, _extends({
    type: "text",
    field: field,
    onFocus: handleFocus,
    onValueChange: handleChange,
    onKeyDownHandler: onKeyDownHandler,
    "data-testid": 'search-input',
    forwardedRef: inputRef,
    inputFocus: inputFocus,
    setInputFocus: setInputFocus,
    getTagApi: getTagApi,
    hideResults: hideResults,
    searchResultsRef: searchResultsRef
  }, rest)) : /*#__PURE__*/React.createElement(TextInput, _extends({
    skinny: true
  }, rest, {
    icon: 'search',
    field: field,
    label: label,
    loading: isLoading,
    onValueChange: handleChange,
    onFocus: handleFocus,
    forwardedRef: inputRef,
    "data-testid": 'search-input',
    onKeyDown: onKeyDownHandler
  })), results.length > 0 && areResultsShown && !disableResultsDropdown && /*#__PURE__*/React.createElement(StyledResultsBox, {
    ref: searchResultsRef,
    "data-testid": 'search-results',
    style: resultBoxStyles
  }, groups && groups.length > 0 ? groups.map(function (group) {
    var filteredResults = results.filter(function (result) {
      var typedResult = result;
      return typedResult.groupId !== undefined && typedResult.groupId === group.id;
    });
    if (filteredResults.length > 0) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ResultGroupComponent, {
        key: v4(),
        group: group
      }), filteredResults.map(function (result) {
        return /*#__PURE__*/React.createElement(ResultComponent, {
          result: result,
          key: v4()
        });
      }));
    }
  }) : results.map(function (result) {
    return /*#__PURE__*/React.createElement(ResultComponent, {
      result: result,
      key: v4()
    });
  })), results.length === 0 && isDirty && debouncedQuery && debouncedQuery.length > 0 && !isLoading && !disableResultsDropdown && areResultsShown && /*#__PURE__*/React.createElement(StyledResultsBox, {
    ref: searchResultsRef,
    "data-testid": 'search-results-empty',
    style: resultBoxStyles
  }, /*#__PURE__*/React.createElement(EmptyResultsComponent, null))));
}

export default Search;
